import React from 'react';
import { durationModerate02 } from '@carbon/motion';
import { InlineNotification, Modal } from '@carbon/react';
import { useCrudConfig } from './provider';

/**
 * How long Carbon's Modal takes to fade out, read from the same motion token its stylesheet
 * uses ($duration-moderate-02) rather than typed as a number here, so a Carbon upgrade that
 * retimes the animation cannot leave this silently stale.
 *
 * It matters because Carbon never unmounts a Modal: it drops `is-visible`, and the children
 * stay mounted and on screen for the whole transition. Anything that changes the modal's
 * content during that window is visible to the user as the body resizing mid-fade.
 *
 * The token describes the transition, not the time until the modal is actually gone: measured
 * in Chromium the fade still had ~10% opacity at 240ms and finished nearer 270ms, because the
 * class flip and the first animation frame both cost time the token does not account for. The
 * small buffer covers that, so the cleanup lands after the modal has left rather than during
 * its last visible frames.
 */
const EXIT_LATENCY_MS = 60;

export const MODAL_EXIT_MS = parseInt(durationModerate02, 10) + EXIT_LATENCY_MS;

export interface CrudFormModalProps {
  open: boolean;
  editing: boolean;
  heading: string;
  label: string;
  submitting: boolean;
  errorMessage: string | null;
  /** The Formik form's imperative handle: { submitForm, resetForm, setFieldValue }. */
  formRef: React.MutableRefObject<any>;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Submits the form behind an imperative ref, or throws saying why it could not.
 *
 * Deliberately NOT `formRef.current?.submitForm()`: a ref that was never attached to the
 * form's innerRef would make the primary button a silent no-op — no error, no feedback, the
 * modal just sitting there looking functional. Failing loudly turns a mystery into a stack
 * trace.
 *
 * Exported so the invariant is directly testable. React intercepts errors thrown inside event
 * handlers and re-raises them asynchronously, so `expect(() => fireEvent.click(...)).toThrow()`
 * can never observe this — the assertion has to call the function itself.
 *
 * The returned promise is deliberately swallowed: Formik's imperative submitForm rejects with
 * the onSubmit error (only its own <form onSubmit> path attaches a catch), and that reason is
 * already rendered from the mutation's own error state.
 */
export function submitViaRef(formRef: React.MutableRefObject<any>): void {
  const form = formRef.current;
  if (!form || typeof form.submitForm !== 'function') {
    throw new Error("CrudFormModal: formRef is not wired to the form's innerRef, so submitting would do nothing.");
  }
  // Formik's imperative submitForm REJECTS with the onSubmit error — only its own handleSubmit
  // attaches a catch, so an ignored promise becomes an unhandledRejection (and a Sentry event)
  // on every refused save. The reason is already rendered from state.saveError, so swallow it;
  // do NOT drop the call itself, or a form whose ref is unwired stops failing loudly.
  const submitted = form.submitForm();
  if (submitted && typeof submitted.catch === 'function') {
    submitted.catch(() => undefined);
  }
}

export const CrudFormModal = React.memo((props: CrudFormModalProps) => {
  const { translate } = useCrudConfig();

  const primaryLabel = props.submitting
    ? `${translate('common.loading')}...`
    : translate(props.editing ? 'actions.update' : 'actions.create');

  return (
    <Modal
      open={props.open}
      size={'sm'}
      modalHeading={props.heading}
      modalLabel={props.label}
      primaryButtonText={primaryLabel}
      secondaryButtonText={translate('actions.cancel')}
      primaryButtonDisabled={props.submitting}
      onSecondarySubmit={props.onClose}
      onRequestClose={props.onClose}
      onRequestSubmit={() => submitViaRef(props.formRef)}
    >
      {props.errorMessage && (
        <InlineNotification kind={'error'} lowContrast hideCloseButton title={''} subtitle={props.errorMessage} />
      )}
      {props.children}
    </Modal>
  );
});
CrudFormModal.displayName = 'CrudFormModal';
